import { Nullable } from '@tcc/common';
import {
  MachineDataMap,
  Legend,
  CategoricalAssetLegend,
  LegendItemType,
  ContinuousAssetLegend,
  AgVizPopulationLayers,
  MixedAssetLegend,
  BaseAssetLegendItem,
  CategoricalGeospatialAssetLegendItem,
  ContinuousGeospatialAssetLegendItem,
} from '../requesters/agVizRequester/types';
import { Interval } from '../types';

import { PLANTING } from '../constants/mapLayerIds';
import { LOCALE_GROUPS } from '../constants/units';

const isCategoricalAssetItem = (item: BaseAssetLegendItem): item is CategoricalGeospatialAssetLegendItem =>
  // eslint-disable-next-line no-underscore-dangle
  item.__typename === LegendItemType.Categorical

const isContinuousAssetItem = (item: BaseAssetLegendItem): item is ContinuousGeospatialAssetLegendItem =>
  // eslint-disable-next-line no-underscore-dangle
  item.__typename === LegendItemType.Continuous

export const isCategoricalAssetLegend = (legend: Legend): legend is CategoricalAssetLegend =>
  legend.items.every(isCategoricalAssetItem);

export const isContinuousAssetLegend = (legend: Legend): legend is ContinuousAssetLegend =>
  (legend as ContinuousAssetLegend).items !== undefined && legend.items.every(isContinuousAssetItem);

export const isMixedAssetLegend = (legend: Legend): legend is MixedAssetLegend => 
  legend.items.some(isCategoricalAssetItem) && legend.items.some(isContinuousAssetItem);
  

const transformCategoricalItemToInterval = ({ color, label: stringValue }): Interval => ({
  stringValue,
  r: color.red,
  g: color.green,
  b: color.blue,
});

const transformCategoricalItemsToInterval = (items: ContinuousGeospatialAssetLegendItem[]): Interval[] => {
  const minMap = items.map(({color, min, max}) => {
    const nonNullMax = max !== null ? max : 0;
    const value = min !== null ? min : nonNullMax;

    return { value, color }
  });

  const minMapDesc = minMap.sort((a, b) => b.value - a.value);

  return minMapDesc.map(({value, color}) => ({
    value: String(value),
    r: color.red,
    g: color.green,
    b: color.blue,
  }));
}

export function transformUapiLegendToDmbIntervals(uapiLegend: Legend): Interval[] {
  if (!uapiLegend || !uapiLegend.items || uapiLegend.items.length === 0) {
    return [];
  }

  const categoricalItems = uapiLegend.items.filter((isCategoricalAssetItem)) as unknown as CategoricalGeospatialAssetLegendItem[];
  const continuousItems = uapiLegend.items.filter(isContinuousAssetItem) as unknown as ContinuousGeospatialAssetLegendItem[];

  return [
    ...transformCategoricalItemsToInterval(continuousItems),
    ...(categoricalItems.map(transformCategoricalItemToInterval).reverse())
  ];
}


export interface AgVizTransformedLayer {
  tileUrlTemplate: string;
  thumbnailUrl: string;
  intervals: Interval[];
  units: string;
  version: Nullable<string>;
  localeGroup?: string;
}

const overrideLayersFromIndex = (
  index: AgVizTransformedLayersIndex,
  layersToRename: Record<string, string>,
  layersToRemove: string[]): AgVizTransformedLayersIndex => 
    Object.entries(index).reduce<AgVizTransformedLayersIndex>((accum, [layerName, layerData]) => {
      const shouldRemoveLayer = layersToRemove.includes(layerName);
      if (shouldRemoveLayer) {
       return accum;
      }

      if (layerName in layersToRename) {
        const newLayerName = layersToRename[layerName];
        return {
          ...accum,
          [newLayerName]: layerData,
        };
      }
      
      return {
        ...accum,
        [layerName]: layerData,
      };  
  }, {});

export type AgVizTransformedLayersIndex = Record<string, AgVizTransformedLayer>;

/**
 * Transform UAPI MachineDataMap to a structure suitable for DMB state
 * Returns a map of layer names to their data (tileUrl, legend, thumbnail)
 * 
 * @param {MachineDataMap[]} maps - Array of machine data maps from UAPI
 * @returns {Object} - Object with layer names as keys
 */
export function transformAgVizMapsForState(maps: MachineDataMap[]): AgVizTransformedLayersIndex {
  const defaultResult: AgVizTransformedLayersIndex = {};

  const availableLayers = maps.reduce<AgVizTransformedLayersIndex>((accum, map) => {
    if (map.assets && map.assets.length > 0) {
      const asset = map.assets[0]; // Use the first asset
      
      return {
        ...accum,
        [map.name]: {
          tileUrlTemplate: asset.tileUrlTemplate,
          thumbnailUrl: asset.thumbnailUrl || '',
          intervals: transformUapiLegendToDmbIntervals(asset.legend),
          units: asset.legend.units || '',
          version: asset.version,
        }
      }
    }

    return accum;
  }, defaultResult);

  /**
   * The reason to have different layers is because the machinery used to plant can be a regular seeder,
   * in that case the population is measured in terms of mass/area (kg/ha), or a air seeder,
   * in that case the population is measured in terms of seed count/area (seeds/ha).
   * 
   * We are prioritizing count by seeds over mass, so if both layers are available.
   */
  if (AgVizPopulationLayers.ActualBySeedCount in availableLayers) {
    return overrideLayersFromIndex(
      availableLayers, {
        [AgVizPopulationLayers.ActualBySeedCount]: PLANTING.POPULATION,
        [AgVizPopulationLayers.TargetBySeedCount]: PLANTING.TARGET_POPULATION,
      },[
        AgVizPopulationLayers.ActualByMass,
        AgVizPopulationLayers.TargetByMass,
      ]
    );
  }

  const layers = overrideLayersFromIndex(availableLayers, {
    [AgVizPopulationLayers.ActualByMass]: PLANTING.POPULATION,
    [AgVizPopulationLayers.TargetByMass]: PLANTING.TARGET_POPULATION,
  }, [
    AgVizPopulationLayers.ActualBySeedCount,
    AgVizPopulationLayers.TargetBySeedCount,
  ]);

  // When using mass-based population layers, we need to set the correct locale group
  // to get the proper units displayed in the legend (e.g., kg/ha or lb/ac).
  //
  // This localeGroup is added into the state through layersReducer, when processing
  // AgVizAction.ReceiveAgVizMaps. And the HarvestAndPlantingLegend calls
  // legendUnits:getLayerUnits to get the proper uom.
  if(layers[PLANTING.POPULATION]) {
    layers[PLANTING.POPULATION].localeGroup = LOCALE_GROUPS.POPULATION_WEIGHT
  }

  if(layers[PLANTING.TARGET_POPULATION]) {
    layers[PLANTING.TARGET_POPULATION].localeGroup = LOCALE_GROUPS.POPULATION_WEIGHT
  }

  return layers;
}
